import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';

const ACCENT_VAR = {
  1: 'var(--color-accent-1)',
  2: 'var(--color-accent-2)',
  3: 'var(--color-accent-3)',
  4: 'var(--color-accent-4)',
} as const;

interface TrendProps {
  value: number;
  label?: string;
}

interface Props {
  label: string;
  value: ReactNode;
  caption?: string;
  accent: 1 | 2 | 3 | 4;
  href?: string;
  className?: string;
  icon?: ReactNode;
  trend?: TrendProps;
}

function formatValue(v: ReactNode): ReactNode {
  if (typeof v === 'number') return v.toLocaleString();
  return v;
}

function TrendBadge({ value, label }: TrendProps) {
  const dir = value > 0 ? 'up' : value < 0 ? 'down' : 'flat';
  const arrow = dir === 'up' ? '↑' : dir === 'down' ? '↓' : '–';
  const display = `${arrow} ${Math.abs(value)}%`;
  return (
    <span className="brand-stat-trend" data-dir={dir} title={label}>
      {display}
    </span>
  );
}

export function StatCard({ label, value, caption, accent, href, className, icon, trend }: Props) {
  const inner = (
    <>
      <div className="brand-card-bar" style={{ backgroundColor: ACCENT_VAR[accent] }} />
      {icon && <div className="brand-stat-icon">{icon}</div>}
      <div className="brand-stat-label mb-1">{label}</div>
      <div className="brand-stat">
        {formatValue(value)}
        {trend && <TrendBadge {...trend} />}
      </div>
      {caption && <div className="brand-stat-caption">{caption}</div>}
    </>
  );

  return href ? (
    <Link
      to={href}
      className={clsx('brand-card brand-card-interactive block h-full', className)}
      style={{ textDecoration: 'none', color: 'inherit' }}
      aria-label={`${label}: ${typeof value === 'number' ? value.toLocaleString() : value}${caption ? ` — ${caption}` : ''}`}
    >
      {inner}
    </Link>
  ) : (
    <div className={clsx('brand-card h-full', className)}>
      {inner}
    </div>
  );
}
